import type { UpgradeHighlightReason } from '../types';

export interface SubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  highlightReason?: UpgradeHighlightReason | null;
}

const BASIC_FEATURES = [
  'Up to 1 pet profile',
  'Up to 10 uploaded documents',
  'Manual reminders on device',
  'Encrypted cloud vault (Supabase)',
];

const PREMIUM_FEATURES = [
  'Unlimited pets',
  'Unlimited document storage',
  'AI Veterinary Insights (coming soon)',
  'Priority vault sync & future OCR unlock',
];

/**
 * Premium paywall — Razorpay/Stripe wiring lands later; primary CTA is informational for now.
 */
function SubscriptionModal({ open, onClose, highlightReason }: SubscriptionModalProps) {
  if (!open) return null;

  const banner =
    highlightReason === 'pets'
      ? "You've reached the Basic Vault limit of 1 pet."
      : highlightReason === 'documents'
        ? "You've reached the Basic Vault limit of 10 documents."
        : highlightReason === 'ai'
          ? 'AI Veterinary Insights are part of Premium Vault.'
          : 'Unlock the full PetNoter experience with Premium Vault.';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md animate-fade-in px-4 pb-safe"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg sm:max-w-2xl bg-brand-950 border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto animate-slide-up">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-6 pt-6 pb-4 bg-brand-950/95 border-b border-white/10 backdrop-blur-sm rounded-t-3xl">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-400 mb-1">
              PetNoter Vault
            </p>
            <h2 id="subscription-modal-title" className="font-display text-2xl font-bold text-white">
              Choose your plan
            </h2>
            <p className="text-sm text-white/50 mt-1 max-w-md">{banner}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all"
          >
            ✕
          </button>
        </div>

        <div className="p-6 grid sm:grid-cols-2 gap-4">
          <section
            className={`rounded-2xl border p-5 flex flex-col gap-4 bg-white/[0.03] ${
              highlightReason ? 'border-white/10 opacity-90' : 'border-brand-500/30 ring-1 ring-brand-500/20'
            }`}
          >
            <div>
              <h3 className="font-display text-lg font-bold text-white">Basic Vault</h3>
              <p className="text-sm text-white/45 mt-0.5">Everything you need to try PetNoter</p>
              <p className="mt-3">
                <span className="text-3xl font-bold text-white">Free</span>
                <span className="text-white/40 text-sm ml-1">forever</span>
              </p>
            </div>
            <ul className="space-y-2.5 flex-1">
              {BASIC_FEATURES.map((line) => (
                <li key={line} className="flex gap-2 text-sm text-white/70">
                  <span className="text-brand-400 flex-shrink-0">✓</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary w-full justify-center py-3 text-sm"
            >
              Continue with Basic
            </button>
          </section>

          <section className="rounded-2xl border-2 border-accent-500/50 p-5 flex flex-col gap-4 bg-gradient-to-br from-brand-900/90 via-brand-950 to-black/40 shadow-[0_0_40px_-10px_rgba(245,158,11,0.35)] relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-accent-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent-500 text-brand-950">
                  Recommended
                </span>
              </div>
              <h3 className="font-display text-lg font-bold text-white">Premium Vault</h3>
              <p className="text-sm text-amber-100/70 mt-0.5">
                Protect API-heavy AI features & unlimited storage
              </p>
              <p className="mt-3 flex items-baseline gap-1 flex-wrap">
                <span className="text-3xl font-bold text-accent-400">₹199</span>
                <span className="text-white/50 text-sm">/ month</span>
              </p>
            </div>
            <ul className="space-y-2.5 flex-1 relative">
              {PREMIUM_FEATURES.map((line) => (
                <li key={line} className="flex gap-2 text-sm text-white/85">
                  <span className="text-accent-400 flex-shrink-0">★</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              disabled
              title="Checkout integration coming soon"
              className="relative w-full justify-center py-3 rounded-2xl font-semibold text-brand-950 bg-gradient-to-r from-amber-400 to-amber-500 opacity-70 cursor-not-allowed shadow-lg"
            >
              Upgrade — coming soon
            </button>
            <p className="text-[11px] text-center text-white/35 relative">
              Razorpay / UPI checkout will unlock this button in a future release.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionModal;
